import React, { memo } from 'react';
import ReactDOM from 'react-dom';
import { BET_PRESETS } from '../constants';

const TimeSlotRow = memo(({
    coinId,
    slot,
    isNextFive,
    isLocked,
    isLive,
    isSettled,
    winLoss,
    countdown,
    liveStartPrice,
    historyItem,
    betState,
    pool,
    getSentimentRatio,
    getPayoutRatio,
    handleDotClick,
    handleBetChange,
    submitBet,
    setBets,
    isLastSettled,
    poolTotal
}) => {
    const [pending, setPending] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const [oddsPopup, setOddsPopup] = React.useState(false);
    const [inputFocused, setInputFocused] = React.useState(false);
    const timerRef = React.useRef(null);
    const oddsBtnRef = React.useRef(null);
    const [popupPos, setPopupPos] = React.useState({ top: 0, left: 0 });

    // Countdown effect — ticks only when pending > 0 and NOT paused
    React.useEffect(() => {
        if (pending > 0 && !paused) {
            timerRef.current = setTimeout(() => setPending(p => p - 1), 1000);
        } else if (pending === 0 && !paused && timerRef.current) {
            submitBet(coinId, slot.id);
            timerRef.current = null;
        }
        return () => clearTimeout(timerRef.current);
    }, [pending, paused, coinId, slot.id, submitBet]);

    // Clear unsubmitted bet when slot advances to locked
    React.useEffect(() => {
        if (isLocked && betState && !betState.locked && pending === 0 && !paused) {
            const key = `${coinId}_${slot.id}`;
            setBets(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    }, [isLocked, coinId, slot.id, setBets, pending, paused]);

    // Direction click — sets direction and starts timer
    const handleDirectionClick = (dir) => {
        if (betState?.locked || isLocked || isLive || isSettled) return;
        if (paused) {
            handleDotClick(coinId, slot.id, dir);
            return;
        }
        if (pending > 0) return;

        const current = betState?.direction;
        const newDir = current === dir ? null : dir;
        handleDotClick(coinId, slot.id, newDir);

        if (newDir && betState?.amount) {
            setPaused(false);
            setPending(5);
        }
    };

    // Row click — pause/resume toggle
    const handleRowClick = (e) => {
        if (e.target.closest('input') || e.target.closest('button') || e.target.closest('[data-dir]')) return;
        if (pending > 0 && !paused) {
            setPaused(true);
            clearTimeout(timerRef.current);
            timerRef.current = null;
        } else if (paused && pending > 0) {
            setPaused(false);
        }
    };

    const hasAmount = betState?.amount > 0;
    const isLockSubmitted = betState?.locked;

    // Pool total in credits — pool props store SOL units (×100 = credits)
    const displayPoolTotal = poolTotal !== undefined && poolTotal !== null
        ? Math.round(poolTotal * 100)
        : (pool ? Math.round((pool.up + pool.down) * 100) : null);

    const rowBg =
        betState?.direction === 'UP' ? 'bg-green-500/10'
        : betState?.direction === 'DOWN' ? 'bg-red-500/10'
        : slot.isBirth ? 'bg-blue-500/5 animate-pulse'
        : isNextFive ? 'bg-gradient-to-r from-[rgba(0,229,204,0.04)] to-transparent'
        : 'hover:bg-white/[0.018]';

    return (
        /*
         * 3-column CSS Grid:
         *   Col 1 — TIME badge:  3.5rem  (fixed)
         *   Col 2 — Content:     1fr     (absorbs all compression, overflow-hidden)
         *   Col 3 — ▲▼ buttons:  auto    (browser allocates FIRST — NEVER clipped)
         */
        <div
            key={slot.id}
            onClick={handleRowClick}
            style={{ display: 'grid', gridTemplateColumns: '3.5rem auto 1fr' }}
            className={`transition-all group h-[33px] items-center ${
                (pending > 0 || paused) ? 'cursor-pointer' : ''
            } ${isLastSettled ? '' : 'border-b border-[#1a1d2a]/70'} ${rowBg}`}
        >

            {/* ── COL 1: Time badge ─────────────────────────────────────── */}
            <div className="flex items-center pl-2">
                <span className={`font-mono rounded px-1 py-0.5 text-xs border min-w-[2.8rem] text-center font-bold ${
                    betState?.direction === 'UP'
                        ? 'text-[#00c896] border-[#00c896]/60 bg-[#00c896]/10 shadow-[0_0_10px_rgba(0,200,150,0.2)]'
                        : betState?.direction === 'DOWN'
                            ? 'text-[#ff3b6b] border-[#ff3b6b]/60 bg-[#ff3b6b]/10 shadow-[0_0_10px_rgba(255,59,107,0.2)]'
                            : 'text-gray-400 border-[#1e2333] bg-[#0a0c12]'
                }`}>
                    {slot.time}
                </span>
            </div>

            {/* ── COL 2: ODDS + payout + input (1fr — compresses, never overflows) ── */}
            <div className={`flex items-center h-full min-w-0 overflow-hidden gap-0.5 ${isLocked || isSettled ? 'opacity-80' : ''}`}>

                {/* Sentiment Ratio / ODDS button */}
                {(() => {
                    const ratio = getSentimentRatio(coinId, slot.id);
                    const dir = betState?.direction;
                    const handleOddsClick = () => {
                        if (!oddsPopup && oddsBtnRef.current) {
                            const rect = oddsBtnRef.current.getBoundingClientRect();
                            setPopupPos({ top: rect.bottom + 4, left: rect.left });
                        }
                        setOddsPopup(prev => !prev);
                    };
                    return (
                        <div className="relative flex-shrink-0 w-[48px]">
                            <button
                                ref={oddsBtnRef}
                                onClick={handleOddsClick}
                                className={`w-full py-0.5 rounded border flex items-center justify-center cursor-pointer transition-colors ${
                                    dir === 'UP'
                                        ? 'bg-green-500/15 border-green-500/40 shadow-[0_0_10px_rgba(34,197,94,0.15)]'
                                        : dir === 'DOWN'
                                            ? 'bg-red-500/15 border-red-500/40 shadow-[0_0_10px_rgba(239,68,68,0.15)]'
                                            : 'bg-gray-900/80 border-gray-700 shadow-[0_0_15px_rgba(255,255,255,0.05)] hover:border-gray-500'
                                }`}
                            >
                                <span className={`text-xs font-mono font-normal ${
                                    dir === 'UP' ? 'text-green-400' : dir === 'DOWN' ? 'text-red-400' : 'text-white'
                                }`}>
                                    {ratio}
                                </span>
                            </button>
                            {oddsPopup && ReactDOM.createPortal(
                                <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setOddsPopup(false)}>
                                    <div
                                        className="fixed w-44 rounded border border-gray-600 bg-gray-900 shadow-[0_8px_30px_rgba(0,0,0,0.8)] overflow-hidden"
                                        style={{ top: popupPos.top, left: popupPos.left, zIndex: 9999 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="px-3 py-2 border-b border-gray-700 text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                                            Pool Details
                                        </div>
                                        <div className="px-3 py-2 space-y-1.5">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-semibold text-green-400">▲ UP</span>
                                                <span className="text-sm font-mono font-bold text-green-300">{pool ? (pool.up * 100).toLocaleString(undefined, {maximumFractionDigits: 0}) : '0'}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm font-semibold text-red-400">▼ DOWN</span>
                                                <span className="text-sm font-mono font-bold text-red-300">{pool ? (pool.down * 100).toLocaleString(undefined, {maximumFractionDigits: 0}) : '0'}</span>
                                            </div>
                                            <div className="border-t border-gray-700 pt-1.5 flex justify-between items-center">
                                                <span className="text-sm font-semibold text-white">Total</span>
                                                <span className="text-sm font-mono font-bold text-white">{pool ? ((pool.up + pool.down) * 100).toLocaleString(undefined, {maximumFractionDigits: 0}) : '0'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    );
                })()}

                {/* Potential/Win payout display — fixed width so column never shifts */}
                <div className="w-[72px] flex-shrink-0 overflow-hidden flex flex-col items-end justify-center border-l border-white/5 h-full px-1">
                    <span className={`text-xs font-mono font-normal leading-none tracking-tighter truncate w-full text-right ${
                        betState?.direction
                            ? (betState.direction === 'UP' ? 'text-green-400' : 'text-red-400')
                            : 'text-white/60'
                    }`}>
                        {betState?.amount && betState?.direction
                            ? (Number(betState.amount) * parseFloat(getPayoutRatio(coinId, slot.id, betState.direction))).toFixed(2)
                            : '0'}
                    </span>
                </div>

                {/* Pending countdown spinner */}
                {pending > 0 && !isLockSubmitted ? (
                    <div className="flex flex-col items-center flex-shrink-0 ml-1">
                        <div className="relative flex items-center justify-center w-[18px] h-[18px] mt-0.5">
                            <svg className="w-full h-full -rotate-90 pointer-events-none drop-shadow-md" viewBox="0 0 24 24">
                                <circle className="text-gray-800" strokeWidth="2.5" stroke="currentColor" fill="transparent" r="10" cx="12" cy="12" />
                                <circle
                                    className={`transition-all duration-1000 ease-linear ${paused ? 'text-orange-500' : 'text-yellow-500'}`}
                                    strokeWidth="2.5" strokeLinecap="round" stroke="currentColor" fill="transparent" r="10" cx="12" cy="12"
                                    style={{ strokeDasharray: 62.83, strokeDashoffset: 62.83 - ((pending / 5) * 62.83) }}
                                />
                            </svg>
                            <span className={`absolute text-[8px] font-bold ${paused ? 'text-orange-400' : 'text-yellow-500'}`}>
                                {pending}
                            </span>
                        </div>
                        <span className={`text-[6px] font-bold uppercase mt-[1px] ${paused ? 'text-orange-400' : 'text-yellow-500'}`}>
                            {paused ? 'GO' : 'STOP'}
                        </span>
                    </div>
                ) : null}

                {/* Amount input — now lives in col 3 with the arrows */}
                {/* ▲ INPUT ▼ — immediately right of CREDITS, left-aligned */}
                {(
                    <div className="flex items-center gap-[2px] ml-1 flex-shrink-0" data-dir="true">
                        {/* UP ▲ */}
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!hasAmount) return;
                                if (isLockSubmitted && betState?.direction === 'DOWN') return;
                                handleDirectionClick('UP');
                            }}
                            className={`transition-all select-none inline-flex items-center justify-center w-8 h-7 rounded-md ${
                                (isLockSubmitted && betState?.direction === 'DOWN')
                                    ? 'bg-gray-900/60 border border-gray-800/40 text-gray-800 cursor-default'
                                    : betState?.direction === 'UP'
                                        ? 'bg-green-500/20 border border-green-500/50 text-green-400 cursor-pointer shadow-[0_0_10px_rgba(34,197,94,0.3)] scale-105'
                                        : !hasAmount
                                            ? inputFocused
                                                ? 'bg-green-500/10 border border-green-500/30 text-green-500 cursor-pointer hover:bg-green-500/15'
                                                : 'bg-gray-900/60 border border-gray-800/40 text-gray-800 cursor-default'
                                            : 'bg-green-500/8 border border-green-500/20 text-green-700 hover:bg-green-500/15 hover:text-green-400 hover:border-green-500/40 cursor-pointer'
                            }`}
                        >
                            <svg viewBox="0 0 24 24" className="w-3 h-3">
                                <path d="M12 4L3 18h18L12 4z" fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round"/>
                            </svg>
                        </span>
                        {/* Amount Input */}
                        <input
                            type="text"
                            inputMode="numeric"
                            value={betState?.amount ? Number(betState.amount).toLocaleString() : ''}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && paused && pending > 0) setPaused(false);
                            }}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/,/g, '');
                                if (raw === '') { handleBetChange(coinId, slot.id, ''); return; }
                                const val = Math.floor(Number(raw));
                                if (!isNaN(val) && String(val).length <= 5) handleBetChange(coinId, slot.id, val);
                            }}
                            disabled={isLockSubmitted || (pending > 0 && !paused) || isLocked || isLive || isSettled}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                            className={`bg-transparent border-b px-1 w-[4.2rem] text-sm text-center font-mono focus:outline-none transition-colors ${
                                betState?.direction === 'UP' ? 'text-green-500 border-green-500'
                                : betState?.direction === 'DOWN' ? 'text-red-500 border-red-500'
                                : 'text-white border-gray-600'
                            }`}
                        />
                        {/* DOWN ▼ */}
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!hasAmount) return;
                                if (isLockSubmitted && betState?.direction === 'UP') return;
                                handleDirectionClick('DOWN');
                            }}
                            className={`transition-all select-none inline-flex items-center justify-center w-8 h-7 rounded-md ${
                                (isLockSubmitted && betState?.direction === 'UP')
                                    ? 'bg-gray-900/60 border border-gray-800/40 text-gray-800 cursor-default'
                                    : betState?.direction === 'DOWN'
                                        ? 'bg-red-500/20 border border-red-500/50 text-red-400 cursor-pointer shadow-[0_0_10px_rgba(239,68,68,0.3)] scale-105'
                                        : !hasAmount
                                            ? inputFocused
                                                ? 'bg-red-500/10 border border-red-500/30 text-red-500 cursor-pointer hover:bg-red-500/15'
                                                : 'bg-gray-900/60 border border-gray-800/40 text-gray-800 cursor-default'
                                            : 'bg-red-500/8 border border-red-500/20 text-red-700 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/40 cursor-pointer'
                            }`}
                        >
                            <svg viewBox="0 0 24 24" className="w-3 h-3">
                                <path d="M12 20L3 6h18L12 20z" fill="currentColor" stroke="currentColor" strokeWidth="0.5" strokeLinejoin="round"/>
                            </svg>
                        </span>
                    </div>
                )}

                {/* Pool total — fixed width green badge so column always aligns */}
                <span className="w-[52px] flex-shrink-0 font-mono text-[10px] font-semibold text-green-300 tracking-wide ml-1.5 whitespace-nowrap px-1 py-0.5 rounded bg-green-500/15 border border-green-500/30 text-center">
                    {(displayPoolTotal || 0).toLocaleString()}
                </span>
            </div>

            {/* ── COL 3: empty trailing space (1fr) ─────────────────────────────── */}
            <div />

        </div>
    );
});

export default TimeSlotRow;
